import { Component, signal } from '@angular/core';
import { SetupWizard } from '../../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	SetupWizardPayloadObjectKeys,
	SetupWizardActivity,
	SetupWizardActivityFormPayload,
	SetupWizardContentFormPayload,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardUploadCsvButton } from '../../setup-wizard-upload-csv-button/setup-wizard-upload-csv-button';
import { SetupWizardDownloadCsvButton } from '../../setup-wizard-download-csv-button/setup-wizard-download-csv-button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { SetupWizardActivityForm } from '../../../../../forms/setup-wizard/setup-wizard-activity-form/setup-wizard-activity-form';
import { SetupWizardActivitiesTable } from './setup-wizard-activities-table/setup-wizard-activities-table';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { SetupWizardContentForm } from '../../../../../forms/setup-wizard/setup-wizard-content-form/setup-wizard-content-form';

@Component({
	selector: 'app-setup-wizard-activity-editor',
	imports: [
		SetupWizardActivityForm,
		SetupWizardActivitiesTable,
		NzButtonModule,
		SetupWizardUploadCsvButton,
		SetupWizardDownloadCsvButton,
		NzTabsModule,
		NzButtonModule,
		NzTooltipModule,
		SetupWizardContentForm,
	],
	standalone: true,
	templateUrl: './setup-wizard-activity-editor.html',
	styleUrl: './setup-wizard-activity-editor.scss',
})
export class SetupWizardActivityEditor {
	public edit = signal<SetupWizardActivity | null>(null);
	public showForm = signal<boolean>(false);
	public loading = signal<boolean>(false);
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;

	constructor(
		private route: ActivatedRoute,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	public onFormSubmit(payload: SetupWizardActivityFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.activities, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onContentFormSubmit(payload: SetupWizardContentFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.contents, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onFormClear() {
		this.showForm.set(false);
		this.edit.set(null);
	}

	public onAdd() {
		this.showForm.set(true);
		this.edit.set(null);
	}

	public onEdit($event: SetupWizardActivity) {
		if ($event.after || $event.before) {
			this.edit.set($event);
			this.showForm.set(true);
		}
	}

	public async onToggleDisable($event: SetupWizardActivity) {
		const beforeAfter = $event.after || $event.before;

		if (!beforeAfter) {
			throw new Error('No activity data available to update');
		}

		await this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.activities, {
			...beforeAfter,
			active: !beforeAfter.active,
		});
	}

	public async onReset($event: SetupWizardActivity) {
		if (!$event.after) {
			throw new Error('No activity data available to reset');
		}

		await this.setupWizard.resetPayloadItem(SetupWizardPayloadObjectKeys.activities, $event.after);
	}

	public async onRemove($event: SetupWizardActivity) {
		if (!$event.after) {
			throw new Error('No activity data available to remove');
		}

		await this.setupWizard.removePayloadItem(SetupWizardPayloadObjectKeys.activities, $event.after);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
